import { Box, Stack } from '@mui/material';
import { ChangeEvent, Dispatch, FC, SetStateAction, useMemo } from 'react';

import { AdminEntityType } from '@/api/admin/types';
import { EntityApprovalStatus, entityApprovalStatus } from '@/api/entity/types';
import { Input, Select } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { legalEntityTypeOptions } from '@/pages/create-entity/common';

import { ResetFiltersButton } from '../../components/reset-filters-button';
import { columns as entitiesTableColumns } from '../data';

const statusOptions = [
  { value: entityApprovalStatus.Verified, label: 'Verified' },
  { value: entityApprovalStatus.ReVerificationPending, label: 'Re-verification Pending' },
  { value: entityApprovalStatus.AwaitingApproval, label: 'Awaiting Approval' },
  { value: entityApprovalStatus.Rejected, label: 'Rejected' },
  { value: entityApprovalStatus.Draft, label: 'Draft' },
  { value: entityApprovalStatus.Blocked, label: 'Blocked' },
];

const columnsGroups = {
  optional: { label: 'Optional columns' },
  required: { label: 'Always visible' },
};
const requiredColumns = ['legalName', 'shortName', 'actions'];
const columnsOptions = entitiesTableColumns.map((column) => {
  const isRequiredColumn = requiredColumns.includes(column.key);
  return {
    value: column.key,
    label: column.header,
    group: isRequiredColumn ? 'required' : 'optional',
    disabled: isRequiredColumn,
  };
});

export interface EntitiesTabFiltersValue {
  search: string;
  status: EntityApprovalStatus[];
  type: AdminEntityType[];
  columns: string[];
}

interface Props {
  value: EntitiesTabFiltersValue;
  setValue: Dispatch<SetStateAction<EntitiesTabFiltersValue>>;
}

export const entitiesTabFiltersDefaultValue: Omit<EntitiesTabFiltersValue, 'columns'> = {
  search: '',
  status: [],
  type: [],
};

export const EntitiesTabFilters: FC<Props> = ({ value, setValue }) => {
  const isMobile = useIsMobile();
  const { search, status, type, columns } = value;

  const { onSearchChange, onStatusChange, onTypeChange, onColumnsChange, resetFilters } = useMemo(
    () => ({
      onSearchChange: (e: ChangeEvent<HTMLInputElement>) =>
        setValue((prev) => ({ ...prev, search: e.target.value })),
      onStatusChange: (v: string[]) =>
        setValue((prev) => ({ ...prev, status: v as EntityApprovalStatus[] })),
      onTypeChange: (v: string[]) =>
        setValue((prev) => ({ ...prev, type: v as AdminEntityType[] })),
      onColumnsChange: (v: string[]) =>
        setValue((prev) => ({
          ...prev,
          columns: [...new Set(['legalName', 'shortName', 'actions', ...v])],
        })),
      resetFilters: () => setValue((prev) => ({ ...prev, ...entitiesTabFiltersDefaultValue })),
    }),
    [setValue],
  );

  const showResetFilter = !!value.search || value.status.length > 0 || value.type.length > 0;

  return (
    <Stack
      alignItems={isMobile ? 'flex-start' : 'center'}
      gap="40px"
      width="100%"
      flexDirection={isMobile ? 'column' : 'row'}
    >
      <Stack width={isMobile ? '100%' : '410px'}>
        <Input placeholder="Search" value={search} onChange={onSearchChange} />
      </Stack>

      <Box
        display="grid"
        gridTemplateColumns={isMobile ? '300px' : '190px 190px 190px auto'}
        gap="16px"
      >
        <Select
          multiple
          showSelectAll
          placeholder="Status"
          options={statusOptions}
          value={status}
          onChange={onStatusChange}
        />

        <Select
          multiple
          showSelectAll
          placeholder="Type"
          options={legalEntityTypeOptions}
          value={type}
          onChange={onTypeChange}
        />

        <Select
          multiple
          showSelectAll
          showSearch
          placeholder="Columns"
          options={columnsOptions}
          groups={columnsGroups}
          value={columns}
          onChange={onColumnsChange}
        />

        {showResetFilter && <ResetFiltersButton onClick={resetFilters} />}
      </Box>
    </Stack>
  );
};
